// Customer-friendly specification labels + key-spec selection.
//
// Product docs carry raw feed attribute keys ("Stone Type_1",
// "Clasp /Connector", "Feature 2"). This module maps them to clean
// customer-facing labels, dedupes multi-stone rows, and picks the short
// "key specs" list for the purchase area. Underlying data is untouched —
// everything else stays available under "View Full Specifications".

const LABEL_MAP = {
  "material": "Material",
  "product type": "Style",
  "jewelry type": "Jewelry Type",
  "weight": "Approximate Weight",
  "finish": "Finish",
  "feature": "Feature",
  "width of item": "Width",
  "length of item": "Length",
  "chain length": "Chain Length",
  "chain width": "Chain Width",
  "chain type": "Chain Type",
  "clasp /connector": "Closure",
  "clasp/connector": "Closure",
  "earring closure": "Closure",
  "earring type": "Earring Style",
  "ring type": "Ring Style",
  "bracelet type": "Bracelet Style",
  "pendant/charm type": "Pendant Style",
  "bangle type": "Bangle Style",
  "anklet type": "Anklet Style",
  "stone type": "Stone Type",
  "stone shape": "Stone Shape",
  "stone quantity": "Stone Count",
  "stone size": "Stone Size",
  "stone weight": "Total Carat Weight",
  "stone color": "Stone Color",
  "stone color grade": "Stone Color Grade",
  "stone clarity": "Stone Clarity",
  "stone creation method": "Stone Creation",
  "stone treatment": "Stone Treatment",
  "stone cut": "Stone Cut",
  "gender": "Gender",
  "plating": "Plating",
  "plating color": "Plating Color",
  "manufacturing process": "Manufacturing Process",
  "sold by unit": "Sold As",
  "completeness": "Includes",
  "profile type": "Profile",
  "texture": "Texture",
  "bail length": "Bail Length",
  "bail width": "Bail Width",
  "charm/element width": "Element Width",
  "charm/element length": "Element Length",
  "engravable": "Engravable",
  "band width": "Band Width",
  "setting type": "Setting Type",
  "mounting type": "Mounting Type",
  "sizing increment": "Sizing Increment",
};

// Technical keys that mean nothing to shoppers — hidden entirely.
const HIDDEN = new Set([
  "item weight u/m",
  "sizing increment",
  "country of origin (primary)",
  "web ready",
]);

// Feed "Material" rows carry composite structure ("Primary - Purity:10K",
// "Primary:Gold") — the clean Metal value is already shown from p.metal.
const isCompositeMaterial = (base, value) =>
  base === "material" && (/primary/i.test(value) || value.includes(":"));

const baseKey = (k) => (k || "").replace(/_\d+$/, "").trim().toLowerCase();

export const specLabel = (k, isDiamond = false) => {
  const base = baseKey(k);
  if (isDiamond && base === "stone clarity") return "Diamond Clarity";
  if (isDiamond && base === "stone color grade") return "Diamond Color";
  if (isDiamond && base === "stone weight") return "Total Diamond Weight";
  return LABEL_MAP[base] || baseKey(k).replace(/\b\w/g, (c) => c.toUpperCase());
};

// Normalize raw attributes into [{label, value, base}] with dedupe.
export const specList = (p) => {
  const attrs = Array.isArray(p?.attributes) ? p.attributes : [];
  const isDiamond = attrs.some(
    (a) => baseKey(a.k) === "stone type" && /diamond/i.test(a.v || "")
  );
  const out = [];
  const seen = new Set();
  for (const a of attrs) {
    const base = baseKey(a.k);
    if (!base || HIDDEN.has(base)) continue;
    const value = String(a.v || "").trim();
    if (!value) continue;
    if (p?.metal && isCompositeMaterial(base, value)) continue;
    const label = specLabel(a.k, isDiamond);
    const sig = `${label}::${value.toLowerCase()}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.push({ label, value, base });
  }
  return out;
};

// Priority order for the compact key-specs block near the price.
const KEY_SPEC_ORDER = [
  "stone type",
  "stone weight",
  "stone color grade",
  "stone clarity",
  "chain length",
  "length of item",
  "width of item",
  "band width",
  "chain width",
  "weight",
  "finish",
  "clasp /connector",
  "earring closure",
  "stone shape",
];

export const keySpecs = (p) => {
  const specs = specList(p);
  const picked = [];
  for (const key of KEY_SPEC_ORDER) {
    const hit = specs.find((s) => s.base === key && !picked.includes(s));
    if (hit) picked.push(hit);
  }
  return picked.slice(0, 6);
};
